import { Component, OnInit } from '@angular/core';
import { TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { QcQuestions } from '../../../../../models';
import { take } from 'rxjs/operators';
import { AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions, TableRowDelete, TableRowUpdate } from '@aims/common-components';
import { Validators } from '@angular/forms';

@Component({
  selector: 'question',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss']
})
export class QuestionComponent implements OnInit {

  constructor(public transferHttp: TransferHttpService,
              public permissions: PermissionsService) {
  }

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],      // superscript/subscript
      [{ indent: '-1' }, { indent: '+1' }],          // outdent/indent
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],          // dropdown with defaults from theme
      [{ align: [] }],
      ['clean'],                                       // remove formatting button
      ['link']                   // link
    ]
  };

  questionTablePermissions: AimsCommonTablePermissions = {
    create: this.permissions.canCreate(this.permissions.ComponentID.QcChecklists),
    update: this.permissions.canUpdate(this.permissions.ComponentID.QcChecklists),
    delete: this.permissions.canDelete(this.permissions.ComponentID.QcChecklists)
  };
  questionTableHeader: AimsCommonTableHeader[] = [
    { key: 'rowExpand', label: '', type: 'rowExpand', filter: false },
    { key: 'editButton', label: 'Edit', type: 'editButton' },
    { key: 'questionId', label: 'Question #', type: 'number', editable: false, width: 70, },
    { key: 'question', label: 'Question', type: 'dynamic', width: 350, validatorOrOpts: [Validators.required] },
    { key: 'pwsReference', label: 'PWS Ref', type: 'string',  validatorOrOpts: [Validators.maxLength(255)] },
    { key: 'altReference', label: 'Alt Ref', type: 'string', validatorOrOpts: [Validators.maxLength(255)] },
    { key: 'pubReferenceId', label: 'Pub Ref', type: 'dynamic', filterType: 'dynamic', width: 150,
      events: { filterAndSortCompare: (term, el) => term === el.pubReferenceId } },
    { key: 'pubRefParagraph', label: 'Pub Paragraph', type: 'string', validatorOrOpts: [Validators.maxLength(50)], width: 120 },
    { key: 'inspectorElc', label: 'ELC', type: 'boolean', filterType: 'boolean', width: 80 },
    { key: 'inspectorFac', label: 'FAC', type: 'boolean', filterType: 'boolean', width: 80 },
    { key: 'selfInspection', label: 'Self Insp', type: 'boolean', filterType: 'boolean', width: 80 },
    { key: 'preInspection', label: 'Pre Insp', type: 'boolean', filterType: 'boolean', width: 80 },
    { key: 'responsibleAssignmentId', label: 'Responsible Assignment', type: 'dynamic', filterType: 'dynamic', width: 250,
      events: { filterAndSortCompare: (term, el) => term === el.responsibleAssignmentId } },
  ];
  defaultQuestion: QcQuestions = this.newQuestion();

  user = this.transferHttp.getCache("user");

  //Spinner
  spinnerText: string = "Loading Questions...";
  showSpinner: boolean = false;

  newQuestion() {
    let retVal = new QcQuestions();
    retVal.selfInspection = false;
    retVal.preInspection = false;
    retVal.inspectorElc = false;
    retVal.inspectorFac = false;
    return retVal;
  }

  //General Config
  questions = this.transferHttp.getSingle('QualityControl/GetChecklistQuestions');
  areaValues = this.transferHttp.getSingle('QualityControl/GetAreas');
  siteValues = this.transferHttp.getSingle('Sch/GetAllCompanyOrSanSites');

  pubValues = this.transferHttp.getSingle('QualityControl/GetPublicationsForPopUp');
  responsibleAssignmentValues = this.transferHttp.getSingle('QualityControl/GetResponsibleAssignmentsForPopUp');

  responsibleAssignmentLookupColumns:AimsCommonTableHeader[] = [
    { key: 'responsibleAssignment', label: 'Assignment', type:AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'active', label: 'Active', type:AimsCommonTableHeaderType.BOOLEAN, filter: true }
  ];

  publicationLookupColumns:AimsCommonTableHeader[]  = [
    { key: 'pubNum', label: 'Pub. #', type:AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'pubTitle', label: 'Title', type:AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'pubIndexType', label: 'Type', type:AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'active', label: 'Active', type:AimsCommonTableHeaderType.BOOLEAN, filter: true }
  ];

  //***************************
  // Functions
  //***************************
  reload() {
    this.questions = this.transferHttp.getSingle('QualityControl/GetChecklistQuestions');
  }

  saveRow(question, sites, areas) {
    this.spinnerText = "Saving...";
    this.showSpinner = true;

    question.sites = Array.apply(null, sites).filter(o => o.selected && o.value != "null").map(o => parseInt(o.value)).join(',');
    question.areas = Array.apply(null, areas).filter(o => o.selected && o.value != "null").map(o => parseInt(o.value)).join(',');

    //added this bit to set string in database properly, storing this as strings instead of a crossover table is probably dumb
    if (question.sites.length == 0) {
      question.sites = null;
    }
    if (question.areas.length == 0) {
      question.areas = null;
    }
    this.transferHttp.put("QualityControl/SaveChecklistQuestions", question).pipe(take(1)).subscribe(_ => {
      this.reload();
      this.showSpinner = false
     });
  }

  public handleRowUpdate(update: TableRowUpdate): void {
    const updatedQuestion: QcQuestions = update.newRow as QcQuestions;

    this.transferHttp.put("QualityControl/SaveChecklistQuestions", updatedQuestion).pipe(take(1)).subscribe(_ => {
      this.reload();
      this.showSpinner = false;
    });
  }

  public handleRowAdded(update: TableRowUpdate){
    const newQuestion: QcQuestions = update.newRow as QcQuestions;
    this.transferHttp.put("QualityControl/SaveChecklistQuestions", newQuestion).pipe(take(1)).subscribe(_ => {
      this.reload();
      this.showSpinner = false;
    });
  }

  handleRowDelete(deleted: TableRowDelete) {
    const deletedQuestion: QcQuestions = deleted.row as QcQuestions;

    this.transferHttp.delete(`QualityControl/DeleteChecklistQuestion?questionId=${deletedQuestion.questionId}`)
    .subscribe(_ => {
      this.reload();
      this.showSpinner = false;
    });
  }

  ngOnInit() {

  }

}
